import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/shared/ui/alert";
import { Button } from "@/shared/ui/button";
import { useAuth } from "@/features/auth/model/AuthContext";
import { useSessionWeb } from "@/features/auth/hooks/auth.hooks";
import { AuthLayout } from "./AuthLayout";
import { AuthCard, AuthCardHeader, AuthCardContent } from "./AuthCard";
import { LogoHeader, AuthHeader } from "./AuthHeader";
import { useTranslation } from "@/shared/i18n";

function parseHashParams(hash: string): Record<string, string> {
  const params = new URLSearchParams(hash.replace(/^#/, ""));
  return Object.fromEntries(params.entries());
}

export default function AuthCallback() {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const { login: setAuthUser } = useAuth();
  const [authError, setAuthError] = useState<string | null>(null);
  const startedRef = useRef(false);

  const sessionMutation = useSessionWeb({
    onSuccess: (data) => {
      if (data && data.userId) {
        setAuthUser(data);
        if (data.role === "ADMIN") {
          navigate("/", { replace: true });
        } else {
          navigate("/stats", { replace: true });
        }
      }
    },
    onError: (error) => {
      setAuthError(error.message || t("authError"));
    },
  });

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const params = parseHashParams(window.location.hash);
    window.history.replaceState(null, "", window.location.pathname);

    if (params.error_description || params.error) {
      setAuthError(params.error_description || t("authError"));
      return;
    }

    if (!params.access_token || !params.refresh_token) {
      setAuthError(t("authError"));
      return;
    }

    sessionMutation.mutate({
      accessToken: params.access_token,
      refreshToken: params.refresh_token,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthLayout>
      <AuthCard>
        <AuthCardHeader>
          <LogoHeader />
          <AuthHeader title={t("welcomeBack")} description={t("finishingSignIn")} />
        </AuthCardHeader>
        <AuthCardContent>
          {authError ? (
            <div className="space-y-4">
              <Alert variant="destructive" className="bg-red-900/20 border-red-900 text-white">
                <AlertDescription>{authError}</AlertDescription>
              </Alert>
              <Button className="w-full" onClick={() => navigate("/auth", { replace: true })}>
                {t("backToLogin")}
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2 py-6 text-white/70">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>{t("loading")}</span>
            </div>
          )}
        </AuthCardContent>
      </AuthCard>
    </AuthLayout>
  );
}
